import * as React from "react";
import { inject } from "mobx-react";
import { forceCheck } from "react-lazyload";

import { UitkTab, UitkTabs } from "uitk-react-tabs";
import { UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkHeading } from "uitk-react-text";
import { classNames } from "uitk-react-utils";

import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { BlossomComponentsFactory } from "components/flexFramework/domain/BlossomComponentsFactory";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { Tab, TabGroupProps } from "./typings";
import { InsideTabGroupContext } from "components/shared/InsideTabGroupContext/InsideTabGroupContext";
import LinksGridWithDialog from "components/flexComponents/Links/GridView/LinksGridWithDialog";
import {
  PageBackgroundContext,
  BackgroundColorTokens,
} from "src/components/flexComponents/PageLevelProviders/PageBackgroundColor/PageBackgroundContext.tsx";
import { TabContents } from "src/components/flexComponents/TabContents/TabContents";
import { FlexComponent } from "src/typings/flexFramework/FlexComponent";

export const TabGroup = inject("analytics")((props: TabGroupProps) => {
  const { flexComponent, context } = props;
  const groups: Tab[] = flexComponent && flexComponent.groups;
  const model = flexComponent && flexComponent.model;

  if (!(groups && groups.length && model)) {
    return null;
  }

  /**
   * inspects provided components and returns a de-duped array containing only TAB type components that have children
   * @param components
   * @returns FlexComponent[]
   */
  const filterTabs = (components: FlexComponent[]): FlexComponent[] => {
    // Tabs cannot have the same name and must contain at least 1 valid module
    const tabNames = new Set();

    return components.filter((component) => {
      // Verify the items in children are tabs, tab name isn't duplicated, and tab has modules
      if (
        !component.model.fmType ||
        (component.model.fmType && component.model.fmType !== "TAB") ||
        !component.name ||
        tabNames.has(component.name) ||
        !component.modules
      ) {
        return false;
      }

      // Verify the number of valid modules, there's no way to know if the render function of a component
      // will return a null value, so the decision is based on other metadata, as well as verifying the module is supported.
      const validModules = BlossomComponentsFactory.get({ flexComponents: component.modules }).filter(
        (module) => module.shouldRender() && module.getReactComponent()
      );

      if (validModules.length < 1) {
        return false;
      }

      tabNames.add(component.name);

      return true;
    });
  };

  const handleTabSelect = (currentSelectedTab: any): void => {
    const selectedTabName = currentSelectedTab as string;
    if (!selectedTabName) {
      return;
    }

    const selectedTab = filteredTabs.find((tab) => tab.name === selectedTabName);
    if (!selectedTab) {
      return;
    }

    const selectedTabIndex = filteredTabs.indexOf(selectedTab);

    const trackingInfo: FlexTrackingInfo = {
      moduleName: flexComponent.id,
      action: Action.CLICK,
      rfrr: `tab.${selectedTabIndex}`,
    };
    sendDelayedTrackEvent(trackingInfo, props.analytics);

    scrollTabsIfNeeded(selectedTabIndex);
    setCurrentTab(selectedTabName);
  };

  /**
   * This method is used to horizontally scroll the currently selected tab
   * into view. As of version 2.0.0, uitk-react-tabs do not have any scrolling functionality
   * so this is a custom one. If the selected tab isn't completely into view, it will scroll it
   * into view with some extra space so the next tab in the current direction is visible (given
   * there is one).
   * This method is to be tested with an e2e test, but we encounter memory errors with the cypress
   * version we use (3.1.5), the test can be found here and should be added back when the memory issue is fixed.
   * https://github.expedia.biz/Flex/blossom-flex-ui/blob/2ec61eb03ad1549c9097b64922a773e9a0dacbf4/test/integration/e2e/TabGroup.spec.ts
   */
  /* istanbul ignore next line */
  const scrollTabsIfNeeded = (selectedTabIndex: number): void => {
    // Cannot get a `ref` on those since they are from uitk-react
    const tabsContainer = document.querySelector(".uitk-tabs");
    const tabs = document.querySelectorAll(".uitk-tab");
    if (!tabsContainer || !tabs || tabs.length <= selectedTabIndex || selectedTabIndex < 0) {
      return;
    }

    const selectedTab = tabs[selectedTabIndex];
    const containerBoundingRect = tabsContainer.getBoundingClientRect();
    const selectedTabBoundingRect = selectedTab.getBoundingClientRect();

    if (!(containerBoundingRect && selectedTabBoundingRect)) {
      return;
    }
    const { right: containerRight, left: containerLeft } = containerBoundingRect;
    const { right: tabRight, left: tabLeft } = selectedTabBoundingRect;

    // Difference in pixel between the left/right edges of the tab and the container, negative means tab is out of container
    const diffLeft = tabLeft - containerLeft;
    const diffRight = containerRight - tabRight;

    // Assume that they cannot both be negative (out of container), it would mean that the title cannot even fit in the container
    if (diffLeft < 0) {
      const left = selectedTabIndex > 0 ? diffLeft - 50 : diffLeft;
      tabsContainer.scrollBy({ left, behavior: "smooth" });

      return;
    }

    if (diffRight < 0) {
      const left = selectedTabIndex < tabs.length - 1 ? 50 - diffRight : -diffRight;
      tabsContainer.scrollBy({ left, behavior: "smooth" });
    }
  };

  const filteredTabs = filterTabs(flexComponent.groups);
  if (!filteredTabs.length) {
    return null;
  }

  const [currentTab, setCurrentTab] = React.useState(filteredTabs[0].name);

  React.useEffect(() => {
    // Forces the lazy loaded images check to see if they need to be loaded
    // when the current tab changes
    forceCheck();
  }, [currentTab]);

  const keyHelper = new ItemKeyHelper("tab");
  const { title, view, dynamicHeight, boxType } = model;
  const tabGroupClasses = classNames("TabGroup uitk-cell all-cell-1-1 all-y-padding-three all-x-padding-three", {
    affinityTabGroup: view === "affinity",
    dynamicHeightTabGroup: dynamicHeight,
  });

  if (view === "row-trigger" || view === "standard-link") {
    const linksCardView = view === "standard-link" && filteredTabs.length === 1 ? "no-card" : "";

    return (
      <div
        className={`${tabGroupClasses} ${linksCardView}`}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
      >
        <UitkCardAloha>
          <UitkCardAlohaContentSection>
            <div className="uitk-cell">
              <LinksGridWithDialog view={view} groups={filteredTabs} id={flexComponent.id} context={context} />
            </div>
          </UitkCardAlohaContentSection>
        </UitkCardAloha>
      </div>
    );
  }

  const backgroundColor = React.useContext(PageBackgroundContext);
  const withBorder = backgroundColor === BackgroundColorTokens.BACKGROUND_WHITE;
  const uitkCardClasses = classNames({ cardBorder: boxType === "bordered-cards" });

  return (
    <div className={tabGroupClasses} data-fm={flexComponent.fmId} data-fm-title-id={flexComponent.fmTitleId}>
      <UitkCardAloha border={withBorder} className={uitkCardClasses}>
        <UitkCardAlohaContentSection>
          <UitkHeading level={2} size={600}>
            {title}
          </UitkHeading>

          <div className="uitk-cell">
            <InsideTabGroupContext.Provider value={true}>
              <UitkTabs selectedTab={currentTab} onTabSelect={handleTabSelect}>
                {filteredTabs.map((tab, index) => (
                  <UitkTab
                    name={tab.name}
                    key={keyHelper.next()}
                    targetURI="#"
                    tabLinkAttr={{
                      id: `tab-${flexComponent.id}-tab-${index + 1}`,
                      "data-section-id": `#section-${flexComponent.id}-tab-${index + 1}`,
                    }}
                  >
                    <TabContents flexComponent={tab} context={context} />
                  </UitkTab>
                ))}
              </UitkTabs>
            </InsideTabGroupContext.Provider>
          </div>
        </UitkCardAlohaContentSection>
      </UitkCardAloha>
    </div>
  );
});

export default TabGroup;
